import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, map, tap, switchMap } from 'rxjs/operators';

import { LocalStorageService, UtilsService } from '@craig/core';
import { STORAGE_AUTH_TOKEN } from '../../../../core/constants/craig.constants';
import { ChatMessage, ChatBotResponse, ErrorResponse } from '@app/interfaces';
import { ResponseFeedbackPayload } from '../active-conversation/response-feedback/response-feedback.interface';
import { ConversationHistoryStorageService } from '../conversation-history/conversation-history-storage.service';
import { StreamingConfigService } from '../config/streaming-config.service';
import { ERC_LOGIN_URL_TOKEN } from '../../../../injection-tokens';

/**
 * Unified HTTP client service for @streaming-view module
 * Single source of truth for:
 * - Authentication (token retrieval and header construction)
 * - Session management (401 handling, token refresh)
 * - Error handling and status code interpretation
 * - Network I/O for both regular HTTP and SSE streaming
 */
@Injectable()
export class AuthClientService {
    private readonly defaultHeaders = {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
    };

    constructor(
        private readonly httpClient: HttpClient,
        private readonly localStorageService: LocalStorageService,
        private readonly utilsService: UtilsService,
        private readonly conversationStorageService: ConversationHistoryStorageService,
        private readonly streamingConfigService: StreamingConfigService,
        @Inject(ERC_LOGIN_URL_TOKEN) private readonly ercLoginUrl: string
    ) {}

    // ==================== CORE HTTP INFRASTRUCTURE ====================

    /**
     * Get auth headers for any HTTP request (regular or SSE)
     * Single source of truth for header construction
     */
    getAuthHeaders(additionalHeaders?: Record<string, string>): Record<string, string> {
        const authToken = this.localStorageService.getRawItem(STORAGE_AUTH_TOKEN);

        // console.log('🔍 AuthClientService - Building auth headers');
        // console.log('🔍 AuthClientService - Auth Token:', authToken ? 'Present' : 'Missing');

        const headers: Record<string, string> = {
            ...this.defaultHeaders,
            ...(additionalHeaders || {})
        };

        if (authToken) {
            headers['Authorization'] = authToken;
        }

        return headers;
    }

    /**
     * Standard POST request with full auth and error handling
     */
    post<T>(url: string, body: any): Observable<HttpResponse<T>> {
        const headers = new HttpHeaders(this.getAuthHeaders());

        // console.log('🔍 AuthClientService - POST request:', url);
        // console.log('🔍 AuthClientService - Request body:', body);

        return this.httpClient
            .post<T>(url, body, {
                observe: 'response',
                headers,
                withCredentials: true
            })
            .pipe(
                tap((response: HttpResponse<T>) => {
                    // console.log('🔍 AuthClientService - POST response status:', response.status);
                }),
                map((response: HttpResponse<T>) => response),
                catchError((err: HttpErrorResponse) => this.errorHandler(err))
            );
    }

    // ==================== APPLICATION-LEVEL METHODS ====================

    /**
     * Send query (legacy non-streaming)
     */
    sendQuery(message: ChatMessage): Observable<ChatBotResponse | null> {
        const messages: ChatMessage[] = this.conversationStorageService.getCurrentConversationMessages();
        const chatEndpoint = '/api/chat';
        return this.post<ChatBotResponse>(chatEndpoint, [...messages, message]).pipe(
            map(({ body }: HttpResponse<ChatBotResponse>) => body)
        );
    }

    /**
     * Send response feedback
     */
    sendResponseFeedback(payload: ResponseFeedbackPayload): Observable<any> {
        const feedbackEndpoint = '/api/responses-agent/feedback';
        return this.post<any>(feedbackEndpoint, payload).pipe(map(({ body }: HttpResponse<any>) => body));
    }

    // ==================== ERROR HANDLING ====================

    /**
     * Handle HTTP errors (regular requests)
     * Single source of truth for error interpretation and session management
     * Follows same pattern as HttpWrapperService for consistency
     */
    private errorHandler(err: HttpErrorResponse): Observable<never> {
        console.error('🚨 HTTP Error in AuthClientService:', err);

        const error: ErrorResponse | string = this.getParsedError(err.error);
        let errorMsg: string = err.message;

        // Check for structured error response with navigation URL
        if (!!error && typeof error !== 'string' && !!error.detail) {
            if (error.detail.error_message) {
                errorMsg = error.detail.error_message;
            }

            // Backend provided a redirect URL (for 401/403 cases)
            if (error.detail.navigate_url) {
                console.warn('🚨 Auth error - redirecting to:', error.detail.navigate_url);
                this.navigateToErrorUri(error.detail.navigate_url).subscribe();
            }
        }
        // Handle 401 Unauthorized - session expired
        else if (err.status === HttpStatusCode.Unauthorized) {
            console.warn('🚨 401 Unauthorized - session expired, clearing auth data and redirecting to ERC login');
            errorMsg = 'Your session has expired. Please log in again.';
            // Clean auth and redirect to ERC login
            this.navigateToErrorUri(this.ercLoginUrl).subscribe();
        }
        // Handle 403 Forbidden - no access to resource
        else if (err.status === HttpStatusCode.Forbidden) {
            console.warn('🚨 403 Forbidden - access denied, clearing auth data and redirecting to ERC login');
            errorMsg = 'You do not have access to this resource. Please contact your administrator.';
            // Clean auth and redirect to ERC login
            this.navigateToErrorUri(this.ercLoginUrl).subscribe();
        }
        // Handle network errors
        else if (err.status === HttpStatusCode.BadGateway || err.status === 0) {
            errorMsg =
                'We encountered a problem getting the chat response. If this persists, please refresh the page or restart the application.';
        }

        return throwError(() => new Error(errorMsg));
    }

    /**
     * Handle SSE stream errors (streaming requests)
     * Centralizes error handling for both HTTP and SSE
     * Returns context-aware user messages based on error type
     */
    handleStreamError(error: any): { shouldRetry: boolean; userMessage: string } {
        console.error('🚨 SSE Stream Error in AuthClientService:', error);

        // Extract HTTP status if available
        const status = error?.response?.status || error?.status;

        // Check for network errors (no status code)
        const isNetworkError = !status && (error?.name === 'TypeError' || error?.message?.includes('fetch'));

        // Handle 401 Unauthorized - session expired, redirect to login after delay
        if (status === HttpStatusCode.Unauthorized || status === 401) {
            console.warn('🚨 401 Unauthorized in stream - session expired, will redirect to ERC login');
            // Show message then clean auth and redirect to ERC login
            timer(3000)
                .pipe(switchMap(() => this.navigateToErrorUri(this.ercLoginUrl)))
                .subscribe();
            return {
                shouldRetry: false,
                userMessage: 'Session expired. Redirecting to login...'
            };
        }

        // Handle 403 Forbidden - no access, redirect to login after delay
        if (status === HttpStatusCode.Forbidden || status === 403) {
            console.warn('🚨 403 Forbidden in stream - access denied, will redirect to ERC login');
            // Show message, then clean auth and redirect to ERC login
            timer(3000)
                .pipe(switchMap(() => this.navigateToErrorUri(this.ercLoginUrl)))
                .subscribe();
            return {
                shouldRetry: false,
                userMessage: 'Access denied. Redirecting to login...'
            };
        }

        // Handle network/connectivity errors (status 0 or no status)
        if (isNetworkError || status === 0) {
            console.warn('🚨 Network error detected - no internet connection or request blocked');
            return {
                shouldRetry: false,
                userMessage: 'Unable to connect to the server. Please check your internet connection and try again.'
            };
        }

        // Handle 500 Internal Server Error
        if (status === HttpStatusCode.InternalServerError || status === 500) {
            return {
                shouldRetry: false,
                userMessage: 'The AI agent is having trouble responding. Please try again in a moment.'
            };
        }

        // Handle 502 Bad Gateway
        if (status === HttpStatusCode.BadGateway || status === 502) {
            return {
                shouldRetry: true,
                userMessage: 'The AI agent is temporarily unavailable. Please try again.'
            };
        }

        // Handle 503 Service Unavailable
        if (status === HttpStatusCode.ServiceUnavailable || status === 503) {
            return {
                shouldRetry: true,
                userMessage: 'The service is temporarily unavailable due to high demand. Please try again in a moment.'
            };
        }

        // Handle 504 Gateway Timeout
        if (status === HttpStatusCode.GatewayTimeout || status === 504) {
            return {
                shouldRetry: true,
                userMessage: 'The request took too long to complete. Please try again.'
            };
        }

        // Handle 429 Too Many Requests
        if (status === 429) {
            return {
                shouldRetry: true,
                userMessage: 'Too many requests. Please wait a moment and try again.'
            };
        }

        // Default error for unknown issues
        return {
            shouldRetry: false,
            userMessage: 'An unexpected error occurred. Please try again or refresh the page.'
        };
    }

    // ==================== PRIVATE HELPER METHODS ====================

    /**
     * Navigate to error URI (redirect to ERC login or backend-specified URL)
     * Follows same pattern as HttpWrapperService
     */
    private navigateToErrorUri(navigateUrl: string): Observable<undefined> {
        // form=1006 means that we will redirect the user to a list of allowed applications.
        // In that case, we don't have to clean local storage
        if (navigateUrl.search('form=1006') === -1) {
            this.utilsService.cleanAuthDataStorage();
        }

        return this.utilsService.redirectToUrl(navigateUrl);
    }

    /**
     * Parse error response to extract structured error information
     */
    private getParsedError(error: string): ErrorResponse | string {
        try {
            return JSON.parse(error);
        } catch {
            return error;
        }
    }
}
